import {Signal, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import {AsyncSubject, merge, Observable, of, Subject} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import type {WebSocketMessage} from '../../../streaming/streaming-interfaces';
import {SubscriptionSubjectType} from '../../../streaming/subscription-subject-type';
import {DeliverableId} from '../types/deliverable';
import {HeldPortfolio} from '../types/held-portfolio';
import {getHeldPositionKey, HeldPosition, HeldPositionIdentifier} from '../types/held-position';
import {LiquidityPool, LiquidityPoolId} from '../types/liquidity-pool';
import {Market} from '../types/market';
import {RefRate} from '../types/ref-rate';
import {Tranche, TrancheId} from '../types/tranche';
import {TrancheDeliverableRules} from '../types/tranche-deliverable-rules';
import {TrancheMarketRules} from '../types/tranche-market-rules';
import {TrancheRules} from '../types/tranche-rules';
import {DexStreamingSubjectService} from './dex-subjects-injection';

enum DexStreamingMdMsgType {
    MARKET_SNAPSHOT                        = 'MARKET_SNAPSHOT',
    MARKET_UPDATE                          = 'MARKET_UPDATE',
    LIQUIDITY_POOL_SNAPSHOT                = 'LIQUIDITY_POOL_SNAPSHOT',
    LIQUIDITY_POOL_UPDATE                  = 'LIQUIDITY_POOL_UPDATE',
    POOL_TRANCHE_SNAPSHOT                  = 'POOL_TRANCHE_SNAPSHOT',
    POOL_TRANCHE_UPDATE                    = 'POOL_TRANCHE_UPDATE',
    POSITION_SNAPSHOT                      = 'POSITION_SNAPSHOT',
    POSITION_UPDATE                        = 'POSITION_UPDATE',
    PORTFOLIO_SNAPSHOT                     = 'PORTFOLIO_SNAPSHOT',
    PORTFOLIO_UPDATE                       = 'PORTFOLIO_UPDATE',
    REFERENCE_RATE_SNAPSHOT                = 'REFERENCE_RATE_SNAPSHOT',
    REFERENCE_RATE_UPDATE                  = 'REFERENCE_RATE_UPDATE',
    POOL_TRANCHE_RULE_SNAPSHOT             = 'POOL_TRANCHE_RULE_SNAPSHOT',
    POOL_TRANCHE_RULE_UPDATE               = 'POOL_TRANCHE_RULE_UPDATE',
    POOL_TRANCHE_DELIVERABLE_RULE_SNAPSHOT = 'POOL_TRANCHE_DELIVERABLE_RULE_SNAPSHOT',
    POOL_TRANCHE_DELIVERABLE_RULE_UPDATE   = 'POOL_TRANCHE_DELIVERABLE_RULE_UPDATE',
    TRADEABLE_MARKET_RULE_SNAPSHOT         = 'TRADEABLE_MARKET_RULE_SNAPSHOT',
    TRADEABLE_MARKET_RULE_UPDATE           = 'TRADEABLE_MARKET_RULE_UPDATE'
}

interface DexStreamingMarketMsg {
    messageType: DexStreamingMdMsgType.MARKET_SNAPSHOT | DexStreamingMdMsgType.MARKET_UPDATE;
    data: Market[];
}

interface DexStreamingLpMsg {
    messageType: DexStreamingMdMsgType.LIQUIDITY_POOL_SNAPSHOT | DexStreamingMdMsgType.LIQUIDITY_POOL_UPDATE;
    data: LiquidityPool[];
}

interface DexStreamingTrancheMsg {
    messageType: DexStreamingMdMsgType.POOL_TRANCHE_SNAPSHOT | DexStreamingMdMsgType.POOL_TRANCHE_UPDATE;
    data: Tranche[];
}

interface DexStreamingHeldPosMsg {
    messageType: DexStreamingMdMsgType.POSITION_SNAPSHOT | DexStreamingMdMsgType.POSITION_UPDATE;
    data: HeldPosition[];
}

interface DexStreamingHeldPortfolioMsg {
    messageType: DexStreamingMdMsgType.PORTFOLIO_SNAPSHOT | DexStreamingMdMsgType.PORTFOLIO_UPDATE;
    data: HeldPortfolio[];
}

interface DexStreamingRefRateMsg {
    messageType: DexStreamingMdMsgType.REFERENCE_RATE_SNAPSHOT | DexStreamingMdMsgType.REFERENCE_RATE_UPDATE;
    data: RefRate[];
}

interface DexStreamingTrancheRuleMsg {
    messageType: DexStreamingMdMsgType.POOL_TRANCHE_RULE_SNAPSHOT | DexStreamingMdMsgType.POOL_TRANCHE_RULE_UPDATE;
    data: TrancheRules[];
}

interface DexStreamingPoolTrancheDeliverableRuleMsg {
    messageType: DexStreamingMdMsgType.POOL_TRANCHE_DELIVERABLE_RULE_SNAPSHOT | DexStreamingMdMsgType.POOL_TRANCHE_DELIVERABLE_RULE_UPDATE;
    data: TrancheDeliverableRules[];
}

interface DexStreamingTradeableMarketRuleMsg {
    messageType: DexStreamingMdMsgType.TRADEABLE_MARKET_RULE_SNAPSHOT | DexStreamingMdMsgType.TRADEABLE_MARKET_RULE_UPDATE;
    data: TrancheMarketRules[];
}

type SubjectMsg = DexStreamingMarketMsg
    | DexStreamingLpMsg
    | DexStreamingTrancheMsg
    | DexStreamingHeldPortfolioMsg
    | DexStreamingHeldPosMsg
    | DexStreamingRefRateMsg
    | DexStreamingTrancheRuleMsg
    | DexStreamingPoolTrancheDeliverableRuleMsg
    | DexStreamingTradeableMarketRuleMsg;

export class DexMdSubjectService implements DexStreamingSubjectService<SubjectMsg> {

    public readonly type: SubscriptionSubjectType<SubjectMsg>;

    public readonly $liquidityPool: Signal<LiquidityPool>;
    public readonly $traches: Signal<Tranche[]>;

    public readonly marketMapById: ReadonlyMap<Market['marketId'], Market>;
    public readonly lpMapById: ReadonlyMap<LiquidityPoolId, LiquidityPool>;
    public readonly trancheMapById: ReadonlyMap<TrancheId, Tranche>;
    public readonly trancheMapByDelId: ReadonlyMap<DeliverableId, Tranche>;
    public readonly posMapById: ReadonlyMap<string, HeldPosition>;
    public readonly posMapByAccountId: ReadonlyMap<HeldPositionIdentifier['accountId'], Map<string, HeldPosition>>;
    public readonly portfolioMapByAccId: ReadonlyMap<HeldPortfolio['accountId'], HeldPortfolio>;
    public readonly refRateMapByPair: ReadonlyMap<string, RefRate>;
    public readonly trancheRulesMapById: ReadonlyMap<TrancheRules['poolTrancheId'], TrancheRules>;
    public readonly trancheDeliverableRulesMapById: ReadonlyMap<TrancheId, Map<DeliverableId, TrancheDeliverableRules>>;
    public readonly trancheMarketRulesMapById: ReadonlyMap<TrancheMarketRules['marketId'], TrancheMarketRules>;

    public readonly lpUpdated$: Observable<void>;
    public readonly tranchesUpdated$: Observable<void>;
    public readonly $tranchesUpdated = signal<void>(undefined, {equal: () => false});
    public readonly positionsUpdated$: Observable<void>;
    public readonly trancheDelRulesUpdated$: Observable<void>;
    public readonly trancheRulesUpdated$: Observable<void>;
    public readonly refRateUpdated$: Observable<RefRate>;

    public readonly lpSnapshotReceived$: Observable<void>;
    public readonly tranchesSnapshotReceived$: Observable<void>;
    public readonly positionsSnapshotReceived$: Observable<void>;
    public readonly trancheDelRulesSnapshotReceived$: Observable<void>;
    public readonly trancheRulesSnapshotReceived$: Observable<void>;
    public readonly refRateSnapshotReceived$: Observable<void>;

    private readonly wMarketMapById = new Map<Market['marketId'], Market>();
    private readonly wLpMapById = new Map<LiquidityPoolId, LiquidityPool>();
    private readonly wTrancheMapById = new Map<TrancheId, Tranche>();
    private readonly wTrancheMapByDelId = new Map<DeliverableId, Tranche>();
    private readonly wPosMapById = new Map<string, HeldPosition>();
    private readonly wPosMapByAccountId = new Map<HeldPositionIdentifier['accountId'], Map<string, HeldPosition>>();
    private readonly wPortfolioMapByAccId = new Map<HeldPortfolio['accountId'], HeldPortfolio>();
    private readonly wRefRateMapByPair = new Map<string, RefRate>();
    private readonly wTrancheRulesMapById = new Map<TrancheRules['poolTrancheId'], TrancheRules>();
    private readonly wTrancheDeliverableRulesMapById = new Map<TrancheId, Map<DeliverableId, TrancheDeliverableRules>>();
    private readonly wTrancheMarketRulesMapById = new Map<TrancheMarketRules['marketId'], TrancheMarketRules>();


    private subscriptionSentSubject = new Subject<any>();
    private disconnectedSubject = new Subject<any>();

    private readonly $wLiquidityPool = signal(undefined);

    private readonly lpUpdatedSubject$ = new Subject<void>();
    private readonly tranchesUpdatedSubject$ = new Subject<void>();
    private readonly positionsUpdatedSubject$ = new Subject<void>();
    private readonly trancheDelRulesUpdatedSubject$ = new Subject<void>();
    private readonly trancheRulesUpdatedSubject$ = new Subject<void>();
    private readonly refRateUpdatedSubject$ = new Subject<RefRate>();

    private readonly lpSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly tranchesSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly positionsSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly trancheDelRulesSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly trancheRulesSnapshotReceivedSubj$ = new AsyncSubject<void>();
    private readonly refRateSnapshotReceivedSubj$ = new AsyncSubject<void>();

    constructor() {
        this.type = this.createType();

        this.lpUpdated$ = this.lpUpdatedSubject$.asObservable();
        this.tranchesUpdated$ = this.tranchesUpdatedSubject$.asObservable();
        this.positionsUpdated$ = this.positionsUpdatedSubject$.asObservable();
        this.trancheDelRulesUpdated$ = this.trancheDelRulesUpdatedSubject$.asObservable();
        this.trancheRulesUpdated$ = this.trancheRulesUpdatedSubject$.asObservable();
        this.refRateUpdated$ = this.refRateUpdatedSubject$.asObservable();

        this.lpSnapshotReceived$ = this.lpSnapshotReceivedSubj$.asObservable();
        this.tranchesSnapshotReceived$ = this.tranchesSnapshotReceivedSubj$.asObservable();
        this.positionsSnapshotReceived$ = this.positionsSnapshotReceivedSubj$.asObservable();
        this.trancheDelRulesSnapshotReceived$ = this.trancheDelRulesSnapshotReceivedSubj$.asObservable();
        this.trancheRulesSnapshotReceived$ = this.trancheRulesSnapshotReceivedSubj$.asObservable();
        this.refRateSnapshotReceived$ = this.refRateSnapshotReceivedSubj$.asObservable();

        this.$liquidityPool = this.$wLiquidityPool.asReadonly();
        this.$traches = toSignal(this.tranchesUpdated$.pipe(
            map(() => [...this.wTrancheMapById.values()])
        ), {initialValue: []});

        this.marketMapById = this.wMarketMapById;
        this.lpMapById = this.wLpMapById;
        this.trancheMapById = this.wTrancheMapById;
        this.trancheMapByDelId = this.wTrancheMapByDelId;
        this.posMapById = this.wPosMapById;
        this.posMapByAccountId = this.wPosMapByAccountId;
        this.portfolioMapByAccId = this.wPortfolioMapByAccId;
        this.refRateMapByPair = this.wRefRateMapByPair;
        this.trancheRulesMapById = this.wTrancheRulesMapById;
        this.trancheDeliverableRulesMapById = this.wTrancheDeliverableRulesMapById;
        this.trancheMarketRulesMapById = this.wTrancheMarketRulesMapById;
    }

    public getRefRateStream(
        dBaseId: DeliverableId,
        dQuoteId: DeliverableId,
        emitEmpty: boolean = false
    ): Observable<RefRate | undefined> {
        const updates$ = this.refRateUpdated$.pipe(
            filter(r => r.baseDeliverableId === dBaseId && r.quoteDeliverableId === dQuoteId)
        );

        const key = this.getRefRatePairKey({baseDeliverableId: dBaseId, quoteDeliverableId: dQuoteId});
        const knownRate = this.refRateMapByPair.get(key);

        return knownRate || emitEmpty
            ? merge(of(knownRate), updates$)
            : updates$;
    }

    public getRefRateByPair(dBaseId: DeliverableId, dQuoteId: DeliverableId): RefRate | undefined {
        const key = this.getRefRatePairKey({baseDeliverableId: dBaseId, quoteDeliverableId: dQuoteId});
        return this.refRateMapByPair.get(key);
    }

    private createType(): SubscriptionSubjectType<SubjectMsg> {
        return {
            name: 'DEX_MD',
            operations: {
                SUBSCRIBE: 'SUBSCRIBE_MARKET_PORTFOLIO',
                UNSUBSCRIBE: 'UNSUBSCRIBE_MARKET_PORTFOLIO'
            },
            messagePipe: _.flow(
                filter((message: WebSocketMessage) => message.messageType in DexStreamingMdMsgType),
                tap((message: SubjectMsg): void => this.readMessage(message))
            ),
            subscriptionSent$: this.subscriptionSentSubject,
            disconnected$: this.disconnectedSubject,
            deprecateSubject: () => this.deprecateSubject()
        };
    }

    private readMessage(message: SubjectMsg): void {
        switch (message.messageType) {

            case DexStreamingMdMsgType.MARKET_SNAPSHOT:
                this.clearMarketStore();
                this.readMarketMessage(message.data);
                break;
            case DexStreamingMdMsgType.MARKET_UPDATE:
                this.readMarketMessage(message.data);
                break;

            case DexStreamingMdMsgType.LIQUIDITY_POOL_SNAPSHOT:
                this.clearLiquidityPoolStore();
                this.readLiquidityPoolMessage(message.data);
                this.lpSnapshotReceivedSubj$.next();
                this.lpSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.LIQUIDITY_POOL_UPDATE:
                this.readLiquidityPoolMessage(message.data);
                break;

            case DexStreamingMdMsgType.POOL_TRANCHE_SNAPSHOT:
                this.clearPoolTrancheStore();
                this.readPoolTrancheMessage(message.data);
                this.tranchesSnapshotReceivedSubj$.next();
                this.tranchesSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.POOL_TRANCHE_UPDATE:
                this.readPoolTrancheMessage(message.data);
                break;

            case DexStreamingMdMsgType.POSITION_SNAPSHOT:
                this.clearPositionStore();
                this.readPositionMessage(message.data);
                this.positionsSnapshotReceivedSubj$.next();
                this.positionsSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.POSITION_UPDATE:
                this.readPositionMessage(message.data);
                break;

            case DexStreamingMdMsgType.PORTFOLIO_SNAPSHOT:
                this.clearPortfolioStore();
                this.readPortfolioMessage(message.data);
                break;
            case DexStreamingMdMsgType.PORTFOLIO_UPDATE:
                this.readPortfolioMessage(message.data);
                break;

            case DexStreamingMdMsgType.REFERENCE_RATE_SNAPSHOT:
                this.clearReferenceRateStore();
                this.readReferenceRateMessage(message.data);
                this.refRateSnapshotReceivedSubj$.next();
                this.refRateSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.REFERENCE_RATE_UPDATE:
                this.readReferenceRateMessage(message.data);
                break;

            case DexStreamingMdMsgType.POOL_TRANCHE_RULE_SNAPSHOT:
                this.clearPoolTrancheRuleStore();
                this.readPoolTrancheRuleMessage(message.data);
                this.trancheRulesSnapshotReceivedSubj$.next();
                this.trancheRulesSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.POOL_TRANCHE_RULE_UPDATE:
                this.readPoolTrancheRuleMessage(message.data);
                break;

            case DexStreamingMdMsgType.POOL_TRANCHE_DELIVERABLE_RULE_SNAPSHOT:
                this.clearPoolTrancheDeliverableRuleStore();
                this.readPoolTrancheDeliverableRuleMessage(message.data);
                this.trancheDelRulesSnapshotReceivedSubj$.next();
                this.trancheDelRulesSnapshotReceivedSubj$.complete();
                break;
            case DexStreamingMdMsgType.POOL_TRANCHE_DELIVERABLE_RULE_UPDATE:
                this.readPoolTrancheDeliverableRuleMessage(message.data);
                break;

            case DexStreamingMdMsgType.TRADEABLE_MARKET_RULE_SNAPSHOT:
                this.clearTradeableMarketRuleStore();
                this.readTradeableMarketRuleMessage(message.data);
                break;
            case DexStreamingMdMsgType.TRADEABLE_MARKET_RULE_UPDATE:
                this.readTradeableMarketRuleMessage(message.data);
                break;
        }
    }

    private clearMarketStore(): void {
        this.wMarketMapById.clear();
    }

    private readMarketMessage(data: Market[]): void {
        data.forEach(item =>
            this.wMarketMapById.set(item.marketId, item)
        );
    }

    private clearLiquidityPoolStore(): void {
        this.wLpMapById.clear();
    }

    private readLiquidityPoolMessage(data: LiquidityPool[]): void {
        data.forEach(item => {
            this.wLpMapById.set(item.liquidityPoolId, item);

            // Notify
            const singleLp = this.$liquidityPool();
            if (!singleLp || item.liquidityPoolId === singleLp.liquidityPoolId) {
                this.$wLiquidityPool.set(item);
            }
        });

        this.lpUpdatedSubject$.next();
    }

    private clearPoolTrancheStore(): void {
        this.wTrancheMapById.clear();
        this.wTrancheMapByDelId.clear();
    }

    private readPoolTrancheMessage(data: Tranche[]): void {
        data.forEach(item => {
            this.wTrancheMapById.set(item.poolTrancheId, item);
            this.wTrancheMapByDelId.set(item.unitDeliverableId, item);
        });

        this.tranchesUpdatedSubject$.next();
        this.$tranchesUpdated.set();
    }

    private clearPositionStore(): void {
        this.wPosMapById.clear();
        this.wPosMapByAccountId.clear();
    }

    private readPositionMessage(data: HeldPosition[]): void {
        data.forEach(item => {
            const posKey = getHeldPositionKey(item.identifier);

            this.wPosMapById.set(posKey, item);

            let accountPositions = this.wPosMapByAccountId.get(item.identifier.accountId);
            if (!accountPositions) {
                accountPositions = new Map<string, HeldPosition>();
                this.wPosMapByAccountId.set(item.identifier.accountId, accountPositions);
            }
            accountPositions.set(posKey, item);
        });

        this.positionsUpdatedSubject$.next();
    }

    private clearPortfolioStore(): void {
        this.wPortfolioMapByAccId.clear();
    }

    private readPortfolioMessage(data: HeldPortfolio[]): void {
        data.forEach(item =>
            this.wPortfolioMapByAccId.set(item.accountId, item)
        );
    }

    private clearReferenceRateStore(): void {
        this.wRefRateMapByPair.clear();
    }

    private readReferenceRateMessage(data: RefRate[]): void {
        data.forEach(item => {
            this.wRefRateMapByPair.set(this.getRefRatePairKey(item), item);
            this.refRateUpdatedSubject$.next(item);
        });
    }

    private clearPoolTrancheRuleStore(): void {
        this.wTrancheRulesMapById.clear();
    }

    private readPoolTrancheRuleMessage(data: TrancheRules[]): void {
        data.forEach(item =>
            this.wTrancheRulesMapById.set(item.poolTrancheId, item)
        );

        this.trancheRulesUpdatedSubject$.next();
    }

    private clearPoolTrancheDeliverableRuleStore(): void {
        this.wTrancheDeliverableRulesMapById.clear();
    }

    private readPoolTrancheDeliverableRuleMessage(data: TrancheDeliverableRules[]): void {
        data.forEach(item => {
            let trancheRec = this.wTrancheDeliverableRulesMapById.get(item.poolTrancheId);

            if (!trancheRec) {
                trancheRec = new Map<DeliverableId, TrancheDeliverableRules>();
                this.wTrancheDeliverableRulesMapById.set(item.poolTrancheId, trancheRec);
            }

            trancheRec.set(item.deliverableId, item);
        });

        this.trancheDelRulesUpdatedSubject$.next();
    }

    private clearTradeableMarketRuleStore(): void {
        this.wTrancheMarketRulesMapById.clear();
    }

    private readTradeableMarketRuleMessage(data: TrancheMarketRules[]): void {
        data.forEach(item =>
            this.wTrancheMarketRulesMapById.set(item.marketId, item)
        );
    }

    private deprecateSubject(): void {
        this.wMarketMapById.clear();
        this.wLpMapById.clear();
        this.wTrancheMapById.clear();
        this.wTrancheMapByDelId.clear();
        this.wPosMapById.clear();
        this.wPosMapByAccountId.clear();
        this.wPortfolioMapByAccId.clear();
        this.wRefRateMapByPair.clear();
        this.wTrancheRulesMapById.clear();
        this.wTrancheDeliverableRulesMapById.clear();
        this.wTrancheMarketRulesMapById.clear();
    }

    private getRefRatePairKey(refRate: Pick<RefRate, 'baseDeliverableId' | 'quoteDeliverableId'>): string {
        return [
            refRate.baseDeliverableId,
            refRate.quoteDeliverableId
        ].join(':');
    }

}
